import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { getNextOccurrenceDate } from "../../lib/recurrence";
import {
  showUndoToast,
  showTaskLinkToast,
  registerPendingWrite,
  clearPendingWrite,
  UNDO_WINDOW_MS,
} from "../../lib/undoStack";
import {
  getCurrentAccessToken,
  keepaliveSupabaseRequest,
} from "../../lib/keepaliveRest";
import { Task } from "../../types";

async function patchTaskInDb(
  id: string,
  fields: Record<string, unknown>,
): Promise<boolean> {
  const { error } = await supabase
    .from("tasks")
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq("id", id);
  return !error;
}

export function useToggleTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return {
    toggleTask: async (id: string): Promise<boolean> => {
      const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const task = tasks.find((t) => t.id === id);
      if (!task) return false;

      const goToTask = () =>
        navigate({
          to: "/task/$taskId",
          params: { taskId: id },
          search: (prev: Record<string, unknown>) => prev,
        });

      const isCompletingNow = !task.completed;

      Sentry.addBreadcrumb({
        category: "task",
        message: "Task toggled",
        data: { taskId: id, completed: isCompletingNow },
        level: "info",
      });

      // Optimistic update for immediate UI response.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        (prev ?? []).map((t) =>
          t.id === id ? { ...t, completed: isCompletingNow } : t,
        ),
      );

      // === Recurring task completion ===
      if (isCompletingNow && task.recurrence_frequency && task.scheduled_date) {
        const nextDate = getNextOccurrenceDate(task.scheduled_date, {
          frequency: task.recurrence_frequency,
          interval: task.recurrence_interval,
          dayOfWeek: task.recurrence_day_of_week,
          endDate: task.recurrence_end_date,
        });

        const ok = await patchTaskInDb(id, { completed: true });
        if (!ok) {
          queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
            (prev ?? []).map((t) =>
              t.id === id ? { ...t, completed: false } : t,
            ),
          );
          Sentry.captureException(new Error("toggleTask DB patch failed"), {
            extra: { mutation: "useToggleTask/recurring", taskId: id },
          });
          showTaskLinkToast({
            prefix: "Failed to complete ",
            label: `"${task.title}"`,
            labelSuffix: ". Please try again.",
            type: "error",
            onLabelClick: goToTask,
          });
          return false;
        }

        let undone = false;
        const accessToken = nextDate ? await getCurrentAccessToken() : null;
        const pendingKey = `task-reschedule-${id}-${Date.now()}`;

        // After the undo window, advance the date to the next occurrence.
        // If no next date exists (end of recurrence), the task stays completed.
        const rescheduleTimer = nextDate
          ? setTimeout(async () => {
              clearPendingWrite(pendingKey);
              if (undone) return;
              const ok2 = await patchTaskInDb(id, {
                completed: false,
                scheduled_date: nextDate,
              });
              if (ok2) {
                queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
                  (prev ?? []).map((t) =>
                    t.id === id
                      ? { ...t, completed: false, scheduled_date: nextDate }
                      : t,
                  ),
                );
              }
              queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
            }, UNDO_WINDOW_MS)
          : null;

        // Flush the reschedule via keepalive if the page unloads mid-window.
        if (rescheduleTimer && accessToken && nextDate) {
          registerPendingWrite(pendingKey, {
            timer: rescheduleTimer,
            flush: () =>
              keepaliveSupabaseRequest(
                `tasks?id=eq.${encodeURIComponent(id)}`,
                {
                  method: "PATCH",
                  body: {
                    completed: false,
                    scheduled_date: nextDate,
                    updated_at: new Date().toISOString(),
                  },
                },
                accessToken,
              ),
          });
        }

        showUndoToast({
          label: `"${task.title}"`,
          labelSuffix: " completed",
          onLabelClick: goToTask,
          onUndo: async () => {
            undone = true;
            if (rescheduleTimer) clearTimeout(rescheduleTimer);
            clearPendingWrite(pendingKey);
            const reverted = await patchTaskInDb(id, { completed: false });
            if (reverted) {
              queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
                (prev ?? []).map((t) =>
                  t.id === id ? { ...t, completed: false } : t,
                ),
              );
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
          },
        });

        return true;
      }

      // === Non-recurring toggle ===
      const ok = await patchTaskInDb(id, { completed: !task.completed });
      if (!ok) {
        queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
          (prev ?? []).map((t) =>
            t.id === id ? { ...t, completed: task.completed } : t,
          ),
        );
        Sentry.captureException(new Error("toggleTask DB patch failed"), {
          extra: { mutation: "useToggleTask/nonRecurring", taskId: id },
        });
        showTaskLinkToast({
          prefix: "Failed to update ",
          label: `"${task.title}"`,
          labelSuffix: ". Please try again.",
          type: "error",
          onLabelClick: goToTask,
        });
        return false;
      }

      if (isCompletingNow) {
        showUndoToast({
          label: `"${task.title}"`,
          labelSuffix: " completed",
          onLabelClick: goToTask,
          onUndo: async () => {
            const reverted = await patchTaskInDb(id, { completed: false });
            if (reverted) {
              queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
                (prev ?? []).map((t) =>
                  t.id === id ? { ...t, completed: false } : t,
                ),
              );
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
          },
        });
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      return true;
    },
  };
}
